import { DecimalPipe } from '@angular/common';
import {
	Component,

	HostListener,
	Injectable,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Optional,

	SimpleChanges
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { cast, IQuestionnaireGroup } from '@interfaces/interfaces';
import { BlockQuestionItem, ChoiceQuestion, RangeQuestion, SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { WorkflowItemKeyDirective } from '@workflow/directives/workflow-item-key/workflow-item-key.directive';
import _ from 'lodash';
import { Subscription } from 'rxjs';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { AbstractSpecialQuestionComponent, ISpecialQuestion, ISpecialQuestionComponent } from '../special-question.service';
import { isNil } from 'lodash';
import { ColorWordEpilogComponent } from '@workflow/components/questionnaire/questionnaire-item/special-question/color-word-test/color-word-epilog.component';

@Injectable()
export class ColorWordTest implements ISpecialQuestion {
	public readonly component = ColorWordTestComponent;
	public readonly specialQuestionType: SpecialQuestionType = 'ColorWordTest';
	public readonly requiredDevices = [];

	title = 'Color-Word-Test';
	missingValueKeyGroup = 'fehlwert';

	public readonly defaultGroupConfiguration: IQuestionnaireGroup = {
		title: 'Color-Word-Test',
		preface: [
			'Der nachfolgende Test gliedert sich in drei Teile, welche Sie einzeln absolvieren. Jeder Teiltest wird Ihnen einzeln erläutert und separat ' +
			'gestartet. Lesen Sie die Aufgabenstellung genau, bevor Sie den einzelnen Test starten.'
		],
		items: [
			cast<BlockQuestionItem>({
				title: 'Color-Word-Test',
				type: 'BlockQuestion',
				items: [
					{
						key: 'zeit1',
						label: 'Verbale Reaktionsfähigkeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 25
						})
					},
					{
						key: 'rotgruenblind',
						label: 'Leiden Sie unter einer Rot-Grün-Sehschwäche oder Rot-Grün-Blindheit?',
						question: cast<ChoiceQuestion>({
							type: 'choice',
							choices: [
								{
									text: 'Ja',
									value: true
								},
								{
									text: 'Nein',
									value: false
								}
							]
						})
					},
				],
				isProcedure: true,
			}),
			cast<BlockQuestionItem>({
				title: 'Color-Word-Test',
				type: 'BlockQuestion',
				showIf: 'colorword.showAdditionalTests',
				items: [
					{
						key: 'zeit2',
						label: 'Kognitive Reaktionsfähigkeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 40
						})
					},
					{
						key: 'zeit3',
						label: 'Kognitive Umstellfähigkeit',
						question: cast<RangeQuestion>({
							type: 'range',
							unit: 'sek',
							rangeLow: 0,
							rangeHigh: 80
						})
					},
				],
				isProcedure: true,
			})
		]};

	public isCompleted(currentValue: {rotgruenblind?: unknown, fehlwert?: boolean}): boolean {
		return currentValue?.fehlwert || currentValue && (
			(currentValue.rotgruenblind && !isNil(currentValue['zeit1'])) ||
			['zeit1','zeit2','zeit3'].every(z => !isNil(currentValue[z]))
		);
	}

	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		const itemCopy = _.cloneDeep((this.defaultGroupConfiguration.items[0] as BlockQuestionItem).items[1]);
		const rotgruenQuestion: BlockQuestionItem = {
			type: (this.defaultGroupConfiguration.items[0] as BlockQuestionItem).type,
			title: (this.defaultGroupConfiguration.items[0] as BlockQuestionItem).title,
			items: [itemCopy],
			options: {wizard: {splitBlock: true}},
		};
		const rotgruenQuestionKey: string = (this.defaultGroupConfiguration.items[0] as BlockQuestionItem).items[1].key;
		rotgruenQuestion.items[0].key = specialQuestion.key+'.'+rotgruenQuestionKey;
		specialQuestion.description = 'Bestätigen Sie den Buzzer, wenn Sie mit dem Vorlesen fertig sind.';
		return [{
			...group,
			question: {
				type: 'prolog',
				title: group.title,
				description: 
					'Im ersten Test werden Ihnen die Worte ROT, BLAU, GRÜN in schwarzen Großbuchstaben in drei Zeilen angezeigt. ' +
					'Bitte lesen Sie genau und so schnell wie möglich Zeile für Zeile laut vor. ' +
					'Die Zeitmessung beginnt, sobald auf Weiter gedrückt wird.' +
					'<br>Abschließend stoppen Sie die Zeitmessung mit dem Buzzer.<br>' +
					'<br>Beispiel:<br>' +
					'![Nur Text](assets/images/descriptions/TextOnly.png)',
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
		},
		{
			...group,
			question: specialQuestion,
			disableNext: true,
			subpage: 1,
			obligatoryUnskippableKeys: [specialQuestion.key+'.zeit1'],
			obligatoryAllKindOfKeys: [specialQuestion.key+'.zeit1'],
			isProcedure: true,
		},
		{
			...group,
			question: rotgruenQuestion,
			subpage: 2,
			obligatoryUnskippableKeys: [rotgruenQuestion.items[0].key],
			obligatoryAllKindOfKeys: [rotgruenQuestion.items[0].key],
			isProcedure: true,
			alignBottom: true,
		},
		{
			...group,
			question: {
				type: 'prolog',
				title: group.title,
				description: 'Im zweiten Test werden Ihnen die Kästchen in den Farben ROT, BLAU und GRÜN angezeigt. ' +
					'Bitte benennen Sie die Farben dieser Kästchen genau und so schnell wie möglich Zeile für Zeile. ' +
					'Die Zeitmessung beginnt, sobald auf Weiter gedrückt wird.' +
					'<br>Abschließend stoppen Sie die Zeitmessung mit dem Buzzer.<br>' +
					'<br>Beispiel:<br>' +
					'![Nur Farbe](assets/images/descriptions/ColorOnly.png)',
				centered: true,
				showIf: specialQuestion.key + '.showAdditionalTests',
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
		},
		{
			...group,
			question: {
				...specialQuestion,
				showIf: specialQuestion.key + '.showAdditionalTests',
			},
			subpage: 2,
			obligatoryUnskippableKeys: [specialQuestion.key+'.zeit2'],
			obligatoryAllKindOfKeys: [specialQuestion.key+'.zeit2'],
			disableNext: true,
			isProcedure: true,
		},
		{
			...group,
			question: {
				type: 'prolog',
				title: group.title,
				description: 'Im dritten Test benennen Sie die Schriftfarbe der einzelnen Wörter. ' +
					'Bitte gehen Sie dabei genau und so schnell wie möglich Zeile für Zeile vor. ' +
					'Die Zeitmessung beginnt, sobald auf Weiter gedrückt wird.' +
					'<br>Abschließend stoppen Sie die Zeitmessung mit dem Buzzer.<br>' +
					'<br>Beispiel:<br>' +
					'![Gefärbter Text](assets/images/descriptions/ColoredText.png)',
				centered: true,
				showIf: specialQuestion.key + '.showAdditionalTests',
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
		},
		{
			...group,
			question: {
				...specialQuestion,
				showIf: specialQuestion.key + '.showAdditionalTests',
			},
			subpage: 3,
			obligatoryUnskippableKeys: [specialQuestion.key+'.zeit3'],
			obligatoryAllKindOfKeys: [specialQuestion.key+'.zeit3'],
			disableNext: true,
			isProcedure: true,
		},];
	}

	buildEpilogPages(
		group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[] {
		return [{
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				centered: true,
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			disableNext: false,
			isProcedure: true,
			alignBottom: true,
			epilogComponent: ColorWordEpilogComponent,
			key: specialQuestion.key,
		}];
	}

}

@Component({
	selector: 'workflow-color-word-test',
	templateUrl: './color-word-test.component.html',
	styleUrls: ['./color-word-test.component.scss',
		'../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class ColorWordTestComponent extends AbstractSpecialQuestionComponent implements OnInit, OnDestroy, OnChanges, ISpecialQuestionComponent {

	results: {
		zeit1: number,
		zeit2: number,
		zeit3: number,
		rotgruenblind: boolean,
	};
	private subscription = new Subscription();
	private startTime: number;
	@Input() page: number;
	private reactPressed = false;
	private rotgruenblind: boolean;

	words = {
		R: 'ROT',
		G: 'GRÜN',
		B: 'BLAU',
	};

	firstPattern = [
		'R','B','G','G','B','B','G','B','G','B',
		'G','R','R','G','R','B','G','G','B','R',
		'G','B','G','B','B','R','B','G','R','B',
	];

	secondPattern = [
		'B','B','R','G','B','R','B','B','R','B',
		'R','R','G','R','B','R','G','B','G','R',
		'R','G','R','R','G','G','R','R','G','G',
	];

	thirdPatternWords = [
		'R','G','G','R','R','B','G','G','R','B',
		'R','R','B','G','B','R','G','G','B','G',
		'B','R','B','G','R','G','B','R','G','G',
	];

	thirdPatternColors = [
		'B','R','B','B','G','R','B','G','B','B',
		'B','G','R','G','G','B','R','B','R','G',
		'R','G','R','G','B','G','G','B','B','G',
	];

	constructor(@Optional() private workflowItemKey: WorkflowItemKeyDirective, router: Router, private route: ActivatedRoute) {
		super(router);
	}

	async dispatchVitaReact($event: Event): Promise<void> {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: Event): void {
		window.dispatchEvent(new Event('vitareact-press'));
		window.dispatchEvent(new Event('vitareact-release'));
		$event.preventDefault();
	}

	@HostListener('window:vitareact-press', ['$event'])
	vitaReactPress($event: Event): void {
		if (!this.reactPressed) {
			this.reactPressed = true;

			if (this.finishedAndLocked) {
				this.goToNext();
			}

			if (this.page === 1) {
				this.results = {
					zeit1: ($event.timeStamp - this.startTime) / 1000,
					zeit2: null,
					zeit3: null,
					rotgruenblind: null,
				};
				this.resultsUpdated();
				this.goToNext();
				return;
			}

			if (!this.results) {
				this.goToProlog();
				return;
			}

			this.results.zeit2 = this.page === 2 ? ($event.timeStamp - this.startTime)/1000 : this.results.zeit2;
			this.results.zeit3 = this.page === 3 ? ($event.timeStamp - this.startTime)/1000 : this.results.zeit3;
			this.resultsUpdated();
			this.goToNext();
			return;
		}
	}

	@HostListener('window:vitareact-release', ['$event'])
	vitaReactRelease(): void {
		this.reactPressed = false;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	ngOnInit(): void {
		this.subscription.add(this.workflowItemKey.propertyFull$.subscribe(data => {
			if (this.finishedAndLocked) {
				this.results = data as {zeit1: number, zeit2: number, zeit3: number, rotgruenblind: boolean};
				this.rotgruenblind = this.results?.rotgruenblind;
			}
		}));

		this.subscription.add(this.workflowItemKey.property$.subscribe(data => {
			if (!this.finishedAndLocked) {
				this.results = data as {zeit1: number, zeit2: number, zeit3: number, rotgruenblind: boolean};
				this.rotgruenblind = this.results?.rotgruenblind;
			}
		}));

		this.startTime = performance.now();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.page) {
			this.startTime = performance.now();
		}
	}

	protected goToNext(): void {
		if (this.results && this.results.rotgruenblind && this.page >= 2) {
			this.goToEpilog();
		} else {
			super.goToNext();
		}
	}

	private resultsUpdated(): void {
		if (this.question?.key && this.results && !this.finishedAndLocked) {
			this.workflowItemKey.setProperty(this.results);
		}
	}

}
